import React, { useState, useEffect } from 'react';
import { Formik } from 'formik';
import { useSelector } from 'react-redux';
import { useParams, useSearchParams } from 'react-router-dom';
import moment from 'moment';

import FormWrapper from '~FormWrapper';
import PatientsTransportsForm from './PatientsTransportsForm';
import { getIsOperatingUnitTransport } from './orgutil';

import { Spinner } from '~ui-kit';

import {
  getUserOrdererOrganizationId,
  getUserSubType,
} from '~features/user/ducks';
import {
  fetchTransferOrganizations,
  fetchOrder,
  // postOrderFormless,
} from '~services/api.helpers';
import { useOnSubmit } from '~hooks/useOnSubmit';
import { useOnEdit } from '~hooks/useOnEdit';
import { removeEmptyKeys } from '../validations';

const equipmentOptions = [
  { value: 'NOTHING', label: 'Ei kuljetusvälinettä' },
  { value: 'BED', label: 'Sänky' },
  { value: 'WHEELCHAIR', label: 'Pyörätuoli' },
  { value: 'WHEELCHAIR_PORTER', label: 'Pyörätuoli, kuljettaja tuo' },
];

// const contentOptions = [
//   { value: 'PATIENT', label: 'Potilas' },
//   { value: 'BED', label: 'Vain sänky' },
//   { value: 'BELONGINGS', label: 'Vain potilaan tavarat' },
//   { value: 'OTHER', label: 'Muu (täsmennä lisätietoihin)' },
// ];

// Example URL
// http://0.0.0.0:5001/palvelut/potilaskuljetukset
// ?transportation-equipment=bed
// &uoma-transfer-id=378231999956040
// &target-uranus-unit=GAS1
// &target-cost-center=295
// &origin-cost-center=114
// &origin-bed=01
// &isolation-precautions=contact%2Cdroplet
// &first-names=Marko+Petteri
// &additional-oxygen=false
// &last-name=M%C3%B6rk%C3%B6
// &origin-uranus-unit=ACUT
// &origin-room=KA

const constructRoom = (room, bed) => {
  if (room && bed) return `${room}:${bed}`;
  if (room) return room;
  if (bed) return bed;
  return '';
};

// Query initial values for deep linking or other integrations
const getQueryValues = (params, organizations, organizationOptions) => {
  const {
    'last-name': queryLastName,
    'first-names': queryFirstNames,
    'origin-cost-center': queryOriginCostCenter,
    // 'origin-uranus-unit': queryOriginUranusUnit,
    'origin-room': queryOriginRoom,
    'origin-bed': queryOriginBed,
    'target-cost-center': queryTargetCostCenter,
    // 'target-uranus-unit': queryTargetUranusUnit,
    'isolation-precautions': queryIsolationPrecautions,
    'transportation-equipment': queryEquipment,
    // 'has-precautions': queryHasPrecautions,
    'additional-oxygen': queryOxygen,
  } = params;

  const patientName =
    queryFirstNames && queryLastName
      ? `${queryFirstNames} ${queryLastName}`
      : null;

  // FROM
  // Check that queryOriginCostCenter exists because otherwise it could match with nulls
  const fromOrganizationObject =
    queryOriginCostCenter &&
    organizations.find(org => org.costCentreCode === queryOriginCostCenter);

  const fromOrganization = organizationOptions.find(
    o => o.value === fromOrganizationObject?.id
  );

  const fromRoom = constructRoom(queryOriginRoom, queryOriginBed);

  // TO
  const toOrganizationObject =
    queryTargetCostCenter &&
    organizations.find(org => org.costCentreCode === queryTargetCostCenter);
  const toOrganization = organizationOptions.find(
    o => o.value === toOrganizationObject?.id
  );

  // PRECAUTIONS
  let precautions = null;
  let contactPrecautions = null;
  let dropletPrecautions = null;
  let airbornePrecautions = null;

  switch (queryIsolationPrecautions) {
    // &isolation-precautions=
    // &isolation-precautions=none
    case '':
    case 'none': {
      precautions = 'NO';
      break;
    }
    // if there is no query parameter
    case undefined: {
      precautions = null;
      break;
    }
    // &isolation-precautions=contact,droplet
    default: {
      const precautionList = queryIsolationPrecautions.split(',');
      precautions = 'YES';

      contactPrecautions = !!precautionList.find(
        precaution => precaution === 'contact'
      );
      dropletPrecautions = !!precautionList.find(
        precaution => precaution === 'droplet'
      );
      airbornePrecautions = !!precautionList.find(
        precaution => precaution === 'airborne'
      );
    }
  }

  const equipmentDict = {
    none: 'NOTHING',
    bed: 'BED',
    wheelchair: 'WHEELCHAIR',
    'wheelchair-porter': 'WHEELCHAIR_PORTER',
  };
  const equipmentValue = queryEquipment && equipmentDict[queryEquipment];
  const equipment = equipmentOptions.find(e => e.value === equipmentValue);

  return {
    patientName,
    fromRoom: fromRoom || null,
    // toRoom,
    fromOrganization,
    toOrganization,
    includeEquipment: equipment ? true : null,
    equipment,
    precautions,
    contactPrecautions,
    dropletPrecautions,
    airbornePrecautions,
    oxygen: queryOxygen === 'true' || null,
  };
};

// Initial values from order that is edited or returned
const getOrderValues = (orderData, organizationOptions) => {
  let orderPrecautions = null;
  if (orderData.precautions === true) orderPrecautions = 'YES';
  else if (orderData.precautions === false) orderPrecautions = 'NO';

  const equipment = equipmentOptions.find(e => e.value === orderData.equipment);
  // const content = contentOptions.find(e => e.value === orderData.content);

  return {
    patientName: orderData.patientName,
    fromRoom: orderData.fromRoom,
    toRoom: orderData.toRoom,
    accuracy: orderData.accuracy,
    fromOrganization: orderData.fromOrganization
      ? organizationOptions.find(
          o => o.value === parseInt(orderData.fromOrganization, 10)
        )
      : null,
    toOrganization: orderData.toOrganization
      ? organizationOptions.find(
          o => o.value === parseInt(orderData.toOrganization, 10)
        )
      : null,
    includePatient: orderData.includePatient,
    includeEquipment: orderData.includeEquipment,
    equipment,
    content: orderData.content,
    includeBelongings: orderData.includeBelongings,
    precautions: orderPrecautions,
    contactPrecautions: orderData.contactPrecautions,
    dropletPrecautions: orderData.dropletPrecautions,
    airbornePrecautions: orderData.airbornePrecautions,
    oxygen: orderData.oxygen,
    time: orderData.time && moment(orderData.time),
    remarks: orderData.remarks,
    ancestorOrderId: orderData.ancestorOrderId,
  };
};

const getDefaultValues = (state, params, organizationOptions) => {
  let fromOrganization = null;
  let toOrganization = null;
  const userOrg = getUserOrdererOrganizationId(state);
  const defaultOrg = organizationOptions.find(o => o.value === userOrg);

  if (getUserSubType(state) === 'UNIT_IMAGING') {
    toOrganization = defaultOrg;
  } else {
    fromOrganization = defaultOrg;
  }

  return {
    patientName: '',
    fromRoom: '',
    toRoom: '',
    accuracy: 'JOUSTAVA',
    fromOrganization,
    toOrganization,
    includePatient: false,
    includeEquipment: false,
    equipment: null,
    content: 'PATIENT',
    includeBelongings: false,
    remarks: '',
    precautions: null,
    contactPrecautions: null,
    dropletPrecautions: null,
    airbornePrecautions: null,
    oxygen: null,
    time: moment().add(15, 'minutes'),
    ancestorOrderId: null,
  };
};

// Removes entries that have a null value from an object
const removeNulls = obj =>
  // eslint-disable-next-line no-unused-vars
  Object.fromEntries(Object.entries(obj).filter(([_, v]) => v != null));

const getInitialValues = (params, state, organizations, orderData) => {
  const organizationOptions = organizations?.map(o => ({
    value: o.id,
    label: o.name,
  }));

  const queryValues = removeNulls(
    getQueryValues(params, organizations, organizationOptions)
  );
  const orderValues = removeNulls(
    getOrderValues(orderData, organizationOptions)
  );
  const defaultValues = getDefaultValues(state, params, organizationOptions);

  // Merge all of the values with respect to query > order > default
  const initialValues = { ...defaultValues, ...orderValues, ...queryValues };

  return initialValues;
};

const validate = values => {
  const errors = {};

  if (!values.patientName) {
    errors.patientName = 'Vaadittu';
  }

  if (!values.fromOrganization) {
    errors.fromOrganization = 'Vaadittu';
  }

  if (!values.toOrganization) {
    errors.toOrganization = 'Vaadittu';
  }

  if (values.content === 'PATIENT' && !values.equipment) {
    errors.equipment = 'Vaadittu';
  }

  if (!values.content) {
    errors.content = 'Vaadittu';
  }

  if (!values.precautions) {
    errors.precautions = 'Vaadittu';
  }

  if (
    values.precautions === 'YES' &&
    !values.contactPrecautions &&
    !values.dropletPrecautions &&
    !values.airbornePrecautions
  ) {
    errors.precautions = `Valitse ainakin yksi eristys tai valitse 'Ei eristystä'`;
  }

  if (!values.time) {
    errors.time = 'Vaadittu';
  }

  if (values.time) {
    const treshold = moment();

    if (values.time.isBefore(treshold)) {
      errors.time = 'Aika ei saa olla menneisyydessä';
    }
  }

  return removeEmptyKeys(errors);
};

const swapToReturnValues = orderData => {
  const newOrderData = {};

  Object.keys(orderData).forEach(key => {
    // Switch locations
    switch (key) {
      case 'fromOrganization':
        newOrderData[key] = orderData.toOrganization;
        break;
      case 'toOrganization':
        newOrderData[key] = orderData.fromOrganization;
        break;
      case 'equipment':
        newOrderData[key] =
          orderData.equipment === 'WHEELCHAIR_PORTER'
            ? 'WHEELCHAIR'
            : orderData.equipment;
        break;
      case 'fromRoom':
        newOrderData[key] = orderData.toRoom;
        break;
      case 'toRoom':
        newOrderData[key] = orderData.fromRoom;
        break;
      case 'accuracy':
        // Accuracy and time should not be passed on
        break;
      case 'time':
        break;
      // case 'precautions':
      //   break;
      // case 'contactPrecautions':
      //   break;
      // case 'dropletPrecautions':
      //   break;
      // case 'airbornePrecautions':
      //   break;
      default:
        newOrderData[key] = orderData[key];
    }
  });

  return newOrderData;
};

const PatientsTransports = props => {
  const { formId, edit, returnOrder } = props;
  const [formOrganizations, setFormOrganizations] = useState();
  const [initialValues, setInitialValues] = useState();
  // const params = qs.parse(props.location.search);
  const [params] = useSearchParams();
  const state = useSelector(state => state);
  const { orderId } = useParams();

  // const userOrganization = useSelector(getUserOrdererOrganizationId);

  // const organizations = useSelector(state => state.reducer.organizations);

  useEffect(() => {
    const fetch = async () => {
      const orgs = await fetchTransferOrganizations(formId);
      let orderData = {};
      if (edit || returnOrder) {
        const order = await fetchOrder(orderId);
        orderData = order.fields || {};

        // If we are making a return order, switch around the locations
        if (returnOrder) {
          orderData = swapToReturnValues(orderData);

          orderData.ancestorOrderId = order.id;
        }
      }

      setFormOrganizations(orgs);
      setInitialValues(getInitialValues(params, state, orgs, orderData));
    };

    try {
      fetch();
    } catch (e) {
      console.error(
        'Error in fetching organizations in patients transports form'
      );
    }
  }, []);

  const onFormSubmit = useOnSubmit();
  const onFormEdit = useOnEdit();

  // Wait until initialValues are ready to initialize Formik
  if (!initialValues) return <Spinner />;

  return (
    <Formik
      initialValues={initialValues}
      validate={validate}
      initialTouched={{
        time: true,
      }}
      onSubmit={async (values, { setSubmitting }) => {
        setSubmitting(true);
        const method = edit ? onFormEdit : onFormSubmit;

        const { ancestorOrderId, ...fields } = values;

        const isOperatingUnitTransport = getIsOperatingUnitTransport(
          fields.fromOrganization,
          fields.toOrganization,
          formOrganizations
        );
        const contentValue = isOperatingUnitTransport
          ? fields.content
          : 'PATIENT';
        const equipmentValue =
          contentValue === 'PATIENT' ? fields.equipment?.value : null;
        const oxygenValue = contentValue === 'PATIENT' ? fields.oxygen : null;

        await method({
          fields: {
            ...fields,
            time: fields.time.toISOString(),
            operatingUnitTransport: isOperatingUnitTransport,
            content: contentValue,
            equipment: equipmentValue,
            oxygen: oxygenValue,
            fromOrganization: fields.fromOrganization.value,
            toOrganization: fields.toOrganization.value,
            precautions: fields.precautions !== 'NO',
            // Make sure we don't pass specific precautions if 'precautions' is NO
            contactPrecautions:
              fields.precautions === 'YES' && fields.contactPrecautions,
            dropletPrecautions:
              fields.precautions === 'YES' && fields.dropletPrecautions,
            airbornePrecautions:
              fields.precautions === 'YES' && fields.airbornePrecautions,
          },
          ancestorOrderId,
          setSubmitting,
          orderId,
          formId,
        });
      }}
    >
      {formikProps => (
        <FormWrapper
          {...props}
          title="Tilaa sisäinen potilaskuljetus"
          formik={formikProps}
          organizations={formOrganizations}
          Component={PatientsTransportsForm}
        />
      )}
    </Formik>
  );
};

export default PatientsTransports;
